/**
 * Goal Selection Wizard
 * Editorial list of journey goals with inline phase strip.
 * Long-form details revealed on demand.
 */

import { useEffect, useRef, useState } from 'react'
import {
  X,
  ArrowRight,
  ChevronDown,
  Compass,
  Search,
  PenTool,
  Megaphone,
  RefreshCw,
  Globe,
  Target,
  Loader2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { cn } from '@/lib/utils'
import type { JourneyGoal, JourneyPhase, PhaseMode } from '@ispot/shared'

interface GoalSelectionWizardProps {
  isOpen: boolean
  onClose: () => void
  onGoalSelected?: (goalId: string) => void
}

const GOAL_ICON: Record<string, LucideIcon> = {
  'idea-to-launch': Compass,
  'validate-problem': Search,
  'design-solution': PenTool,
  'take-to-market': Megaphone,
  'iterate-product': RefreshCw,
  'expand-market': Globe,
}

const STAGE_LABEL: Record<JourneyGoal['type'], string> = {
  greenfield: 'New product',
  brownfield: 'Existing product',
}

const MODE_LABEL: Record<Exclude<PhaseMode, 'skip'>, string> = {
  full: 'Full',
  check: 'Check',
  review: 'Review',
}

function includedPhases(goal: JourneyGoal, phases: JourneyPhase[]): JourneyPhase[] {
  return phases
    .filter(p => goal.phaseConfig[p.id] !== 'skip')
    .sort((a, b) => a.order - b.order)
}

function sortedPhases(phases: JourneyPhase[]): JourneyPhase[] {
  return [...phases].sort((a, b) => a.order - b.order)
}

function groupGoals(goals: JourneyGoal[]): Array<{ stage: JourneyGoal['type']; goals: JourneyGoal[] }> {
  const greenfield = goals.filter(g => g.type === 'greenfield')
  const brownfield = goals.filter(g => g.type === 'brownfield')
  const out: Array<{ stage: JourneyGoal['type']; goals: JourneyGoal[] }> = []
  if (greenfield.length) out.push({ stage: 'greenfield', goals: greenfield })
  if (brownfield.length) out.push({ stage: 'brownfield', goals: brownfield })
  return out
}

interface PhaseStripProps {
  goal: JourneyGoal
  phases: JourneyPhase[]
}

function PhaseStrip({ goal, phases }: PhaseStripProps) {
  const ordered = sortedPhases(phases)
  const includedCount = ordered.filter(p => goal.phaseConfig[p.id] !== 'skip').length
  return (
    <div
      className="flex items-center gap-1.5"
      role="img"
      aria-label={`${includedCount} of ${ordered.length} phases included`}
    >
      {ordered.map(phase => {
        const mode = goal.phaseConfig[phase.id] as PhaseMode | undefined
        const included = mode && mode !== 'skip'
        return (
          <span
            key={phase.id}
            title={`${phase.name}${included ? '' : ' — skipped'}`}
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: included ? phase.color : 'transparent',
              border: included ? 'none' : '1px solid var(--color-border-strong)',
            }}
          />
        )
      })}
    </div>
  )
}

export function GoalSelectionWizard({ isOpen, onClose, onGoalSelected }: GoalSelectionWizardProps) {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const { framework, selectGoal, isUpdating } = useJourneyStore()

  const goals = framework?.goals ?? []
  const phases = framework?.phases ?? []
  const groups = groupGoals(goals)

  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  // Reset on open/close
  useEffect(() => {
    if (!isOpen) {
      setSelectedGoalId(null)
      setShowDetails(false)
    }
  }, [isOpen])

  // Reveal details whenever a goal becomes selected
  useEffect(() => {
    if (selectedGoalId) setShowDetails(true)
  }, [selectedGoalId])

  // Escape closes (but not while a save is in flight)
  useEffect(() => {
    if (!isOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isUpdating) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose, isUpdating])

  // Lock background scroll while open
  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  // Initial focus: first goal if available, else close button
  useEffect(() => {
    if (!isOpen) return
    const firstGoalId = goals[0]?.id
    const target = firstGoalId ? rowRefs.current.get(firstGoalId) : null
    if (target) target.focus()
    else closeBtnRef.current?.focus()
    // Intentionally only re-run on open transition; goals identity is stable per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen || !framework) return null

  const selectedGoal = goals.find(g => g.id === selectedGoalId) ?? null
  const detailPhases = selectedGoal ? includedPhases(selectedGoal, phases) : []

  // Roving-tabindex helper: which goal owns the single tab stop?
  const focusableGoalId = selectedGoalId ?? goals[0]?.id ?? null

  function moveTo(idx: number) {
    if (idx < 0 || idx >= goals.length) return
    const goal = goals[idx]
    setSelectedGoalId(goal.id)
    rowRefs.current.get(goal.id)?.focus()
  }

  async function handleStart() {
    if (!selectedGoalId || !currentAccount?.id || !currentProject?.id || isUpdating) return
    await selectGoal(currentAccount.id, currentProject.id, selectedGoalId)
    onGoalSelected?.(selectedGoalId)
    onClose()
  }

  function handleRowKey(e: React.KeyboardEvent<HTMLButtonElement>, goalId: string) {
    const idx = goals.findIndex(g => g.id === goalId)
    if (idx < 0) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        moveTo(Math.min(goals.length - 1, idx + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        moveTo(Math.max(0, idx - 1))
        break
      case 'Home':
        e.preventDefault()
        moveTo(0)
        break
      case 'End':
        e.preventDefault()
        moveTo(goals.length - 1)
        break
      case 'Enter':
        // Space already triggers click on a button by default; only handle Enter
        // explicitly so it commits the journey instead of just selecting.
        if (selectedGoalId === goalId) {
          e.preventDefault()
          handleStart()
        }
        break
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      {/* Backdrop */}
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close dialog"
        className="absolute inset-0 bg-[rgba(13,23,28,0.12)] cursor-default"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="goal-wizard-title"
        aria-busy={isUpdating || undefined}
        className="relative w-full max-w-[680px] max-h-[min(720px,calc(100dvh-48px))] grid grid-rows-[auto_1fr_auto] overflow-hidden rounded-xl bg-bg-card border border-warm-200 shadow-[0_24px_64px_rgba(13,23,28,0.14),0_4px_12px_rgba(13,23,28,0.06)]"
      >
        {/* Header */}
        <header className="grid grid-cols-[1fr_auto] gap-4 items-start px-7 pt-5 pb-4 border-b border-warm-200">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-1.5">
              New project · Setup
            </div>
            <h2
              id="goal-wizard-title"
              className="text-[22px] font-display font-medium text-text leading-tight tracking-[-0.018em]"
            >
              Choose your journey goal
            </h2>
            <p className="mt-1 text-[14px] text-text-secondary leading-snug max-w-[56ch]">
              Pick the path that matches where you are. You can change it later without losing work.
            </p>
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 grid place-items-center rounded-md text-text-secondary hover:bg-warm-100 hover:text-text transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Body */}
        <div className="overflow-auto px-5 py-5">
          {goals.length === 0 ? (
            <p className="px-3 py-6 text-[13.5px] text-text-muted text-center">
              No journeys are configured yet.
            </p>
          ) : (
            groups.map(({ stage, goals: stageGoals }, groupIdx) => (
              <section key={stage} className={groupIdx > 0 ? 'mt-7' : undefined}>
                <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted px-3 mb-2">
                  {STAGE_LABEL[stage]}
                </div>
                <ul className="grid gap-0.5" role="listbox" aria-label={STAGE_LABEL[stage]}>
                  {stageGoals.map(goal => {
                    const Icon = GOAL_ICON[goal.id] ?? Target
                    const isSelected = selectedGoalId === goal.id
                    const tabIndex = goal.id === focusableGoalId ? 0 : -1
                    return (
                      <li key={goal.id}>
                        <button
                          ref={el => {
                            if (el) rowRefs.current.set(goal.id, el)
                            else rowRefs.current.delete(goal.id)
                          }}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={isUpdating || undefined}
                          tabIndex={tabIndex}
                          onClick={() => {
                            if (isUpdating) return
                            setSelectedGoalId(goal.id)
                          }}
                          onKeyDown={e => handleRowKey(e, goal.id)}
                          className={cn(
                            'relative w-full text-left grid grid-cols-[36px_minmax(0,1fr)_auto] gap-3 items-center px-3 py-3 rounded-md transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
                            isUpdating && 'opacity-60 cursor-not-allowed',
                            isSelected
                              ? 'bg-bg-secondary'
                              : !isUpdating && 'hover:bg-bg-muted',
                          )}
                        >
                          {isSelected && (
                            <span
                              aria-hidden="true"
                              className="absolute start-0 inset-y-2 w-[2px] rounded-r-sm bg-accent"
                            />
                          )}
                          <span
                            className={cn(
                              'shrink-0 w-9 h-9 grid place-items-center rounded-md transition-colors',
                              isSelected
                                ? 'bg-accent/10 text-accent'
                                : 'bg-warm-100 text-text-secondary',
                            )}
                          >
                            <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                          </span>
                          <div className="min-w-0">
                            <div className="font-display font-medium text-[14.5px] text-text leading-tight tracking-[-0.01em] line-clamp-2">
                              {goal.name}
                            </div>
                            <div className="mt-0.5 text-[12.5px] text-text-muted leading-snug line-clamp-2">
                              {goal.shortDescription}
                            </div>
                          </div>
                          <PhaseStrip goal={goal} phases={phases} />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </section>
            ))
          )}

          {/* Progressive disclosure: long-form details */}
          {selectedGoal && (
            <div className="mt-7 mx-3 pt-5 border-t border-warm-200">
              <button
                type="button"
                onClick={() => setShowDetails(s => !s)}
                aria-expanded={showDetails}
                className="inline-flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-[0.1em] text-text-secondary hover:text-text transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 rounded-sm"
              >
                <ChevronDown
                  className={cn(
                    'w-3 h-3 transition-transform duration-200 motion-reduce:transition-none',
                    showDetails && 'rotate-180',
                  )}
                />
                {showDetails ? 'Hide details' : 'Show details'}
              </button>

              {showDetails && (
                <div className="mt-4 grid gap-5">
                  {selectedGoal.description && (
                    <p className="text-[13.5px] text-text-secondary leading-relaxed max-w-[60ch]">
                      {selectedGoal.description}
                    </p>
                  )}

                  {selectedGoal.recommendedFor && selectedGoal.recommendedFor.length > 0 && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
                        Best fit when
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedGoal.recommendedFor.map((useCase, i) => (
                          <span
                            key={i}
                            className="font-mono text-[11px] text-text-secondary bg-bg-secondary px-2 py-0.5 rounded-sm"
                          >
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {detailPhases.length > 0 && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2.5">
                        Phases
                      </div>
                      <ol className="grid gap-2">
                        {detailPhases.map((phase, idx) => {
                          const mode = (selectedGoal.phaseConfig[phase.id] || 'full') as PhaseMode
                          const showModeTag = mode !== 'full' && mode !== 'skip'
                          return (
                            <li
                              key={phase.id}
                              className="grid grid-cols-[22px_minmax(0,1fr)_auto] gap-3 items-baseline"
                            >
                              <span
                                aria-hidden="true"
                                className="w-[22px] h-[22px] rounded-full grid place-items-center text-white font-mono text-[10px] font-medium"
                                style={{ backgroundColor: phase.color }}
                              >
                                {idx + 1}
                              </span>
                              <div className="leading-snug">
                                <span className="font-display text-[13.5px] font-medium text-text">
                                  {phase.name}
                                </span>
                                {phase.objective && (
                                  <span className="text-[12.5px] text-text-muted">
                                    {' — '}
                                    {phase.objective}
                                  </span>
                                )}
                              </div>
                              {showModeTag && (
                                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-text-muted">
                                  {MODE_LABEL[mode as Exclude<PhaseMode, 'skip'>]}
                                </span>
                              )}
                            </li>
                          )
                        })}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 px-6 py-3.5 border-t border-warm-200 bg-bg-muted">
          <button
            type="button"
            onClick={onClose}
            disabled={isUpdating}
            className="px-3.5 py-2 text-[13.5px] text-text-secondary hover:text-text hover:bg-bg-secondary rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={!selectedGoalId || isUpdating}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 text-[13.5px] font-medium rounded-md transition-colors',
              selectedGoalId && !isUpdating
                ? 'bg-accent text-white hover:bg-accent-700'
                : 'bg-warm-200 text-text-muted cursor-not-allowed',
            )}
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none" />
                Saving
              </>
            ) : (
              <>
                Start journey
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  )
}

export default GoalSelectionWizard
